// apps/client/src/components/auth/MfaChallengeCard.tsx
//
// Native GCIP sign-in challenge (mockup screen 2). Completes the second factor
// with the live MultiFactorResolver captured when sign-in threw
// `auth/multi-factor-auth-required`. On success the user is fully signed in and
// the caller navigates onward.
//
// Failure/recovery UX:
//  - wrong / expired code          → inline red boxes + retry (mockup error state)
//  - network / rate limit / other  → a plain, non-code message; boxes stay neutral
//  - stale MFA session             → "Back to sign in" (a fresh challenge)
//  - unsupported factor            → dead end: message + the recovery route only
//  - App Check / reCAPTCHA throttle → AppCheckRecoveryBanner (2026-06-12 lesson)
//  - "Can't access your authenticator?" → MfaRecoveryPanel (mediated reset)
//  - onSuccess rejected AFTER sign-in → "You're signed in" panel: retry the
//    continuation or reload; the spent resolver is never challenged again
//
// The two phases are deliberately separate try/catches (finding M1, 2026-08-17):
// once `resolveSignIn` returns, the user IS authenticated and the resolver is
// spent, so a failure in `onSuccess` must never read as "wrong code" and must
// never invite another code.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { MultiFactorResolver, UserCredential } from 'firebase/auth';
import { Lock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Spinner } from '../common/Spinner';
import MfaCodeInput from './MfaCodeInput';
import MfaRecoveryPanel from './MfaRecoveryPanel';
import { resolveTotpSignIn } from '../../lib/nativeMfa';
import { classifyMfaFailure, mfaFailureMessage, readErrorCode, type MfaFailureKind } from '../../lib/mfaErrors';
import {
  classifyAuthError,
  clearAppCheckThrottleState,
  type AppCheckErrorContext,
} from '../../lib/appCheckRecovery';
import AppCheckRecoveryBanner from './AppCheckRecoveryBanner';

interface MfaChallengeCardProps {
  resolver: MultiFactorResolver;
  /** Called once the second factor resolves and the session is signed in. */
  onSuccess: (credential: UserCredential) => void | Promise<void>;
}

interface ChallengeFailure {
  kind: MfaFailureKind;
  message: string;
}

/** Kinds after which no further code can succeed on this resolver. */
const TERMINAL_KINDS: ReadonlySet<MfaFailureKind> = new Set<MfaFailureKind>([
  'session_expired',
  'requires_recent_login',
  'unsupported_factor',
  'blocked',
]);

const CONTINUE_FAILED_MESSAGE =
  'Your code was accepted and you are signed in, but the next step did not finish.';

const MfaChallengeCard: React.FC<MfaChallengeCardProps> = ({ resolver, onSuccess }) => {
  const [code, setCode] = useState<string>('');
  const [verifying, setVerifying] = useState<boolean>(false);
  const [failure, setFailure] = useState<ChallengeFailure | null>(null);
  const [appCheckCtx, setAppCheckCtx] = useState<AppCheckErrorContext | null>(null);
  const [showRecovery, setShowRecovery] = useState<boolean>(false);
  const [resetNonce, setResetNonce] = useState<number>(0);
  // Set the moment resolveSignIn returns: from here the resolver is spent.
  const [signedIn, setSignedIn] = useState<UserCredential | null>(null);
  const [continueError, setContinueError] = useState<string>('');
  // True only while this card is on screen. A challenge that is still in flight
  // when the card unmounts (the user went back and started a second one) must
  // not run its continuation: React ignores the state writes, but NOT the
  // onSuccess callback, which navigates the app on this stale card's behalf.
  // Set in the effect BODY, never at declaration: React 18 StrictMode runs
  // mount / cleanup / mount, so a declaration-time `true` would be left false.
  const mountedRef = useRef<boolean>(false);
  const signedInHeadingRef = useRef<HTMLHeadingElement | null>(null);

  const isWrongCode = failure?.kind === 'wrong_code';
  const isTerminal = failure !== null && TERMINAL_KINDS.has(failure.kind);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The signed-in panel replaces the whole card, so keyboard and screen reader
  // focus must follow it rather than stay on a control that is gone.
  useEffect(() => {
    if (signedIn) {
      signedInHeadingRef.current?.focus();
    }
  }, [signedIn]);

  // Phase 2: hand the signed-in session to the caller. Retryable on its own.
  const continueAfterSignIn = useCallback(async (credential: UserCredential): Promise<void> => {
    if (!mountedRef.current) return;
    setContinueError('');
    setVerifying(true);
    try {
      await onSuccess(credential);
    } catch (err) {
      if (!mountedRef.current) return;
      // The code, never the raw message: SDK messages are long, English-only
      // and can carry request detail that does not belong on a sign-in screen.
      const code = readErrorCode(err);
      setContinueError(code ? `${CONTINUE_FAILED_MESSAGE} (${code})` : CONTINUE_FAILED_MESSAGE);
    } finally {
      if (mountedRef.current) {
        setVerifying(false);
      }
    }
  }, [onSuccess]);

  // Phase 1: the second factor itself.
  const handleVerify = useCallback(async (submitCode: string = code): Promise<void> => {
    if (submitCode.length !== 6 || verifying || signedIn || isTerminal) return;
    setVerifying(true);
    setFailure(null);
    let credential: UserCredential;
    try {
      credential = await resolveTotpSignIn(resolver, submitCode);
    } catch (err) {
      if (!mountedRef.current) return;
      const ctx = classifyAuthError(err);
      if (ctx.isAppCheckFailure) {
        setAppCheckCtx(ctx);
      } else {
        const classified = classifyMfaFailure(err);
        setFailure({ kind: classified.kind, message: mfaFailureMessage(classified, 'challenge') });
        if (classified.kind === 'wrong_code') {
          // Only a wrong code clears the boxes; anything else was not the code's fault.
          setResetNonce((n) => n + 1);
          setCode('');
        }
      }
      setVerifying(false);
      return;
    }
    if (!mountedRef.current) return;
    setSignedIn(credential);
    await continueAfterSignIn(credential);
  }, [code, verifying, signedIn, isTerminal, resolver, continueAfterSignIn]);

  if (showRecovery) {
    return <MfaRecoveryPanel onBack={() => setShowRecovery(false)} />;
  }

  if (appCheckCtx) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700">
        <AppCheckRecoveryBanner
          ctx={appCheckCtx}
          onReset={async () => {
            await clearAppCheckThrottleState();
            window.location.assign('/login');
          }}
        />
      </div>
    );
  }

  // The second factor is resolved: from here the user is signed in and the
  // resolver is spent, so no code input is ever shown again. While the
  // continuation is in flight this reads "Continuing"; if it fails, the user is
  // told plainly and can retry the continuation itself or reload.
  if (signedIn) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-gradient-to-r from-emerald-500 to-green-500">
            <CheckCircle2 size={28} className="text-white" />
          </div>
          <h2
            ref={signedInHeadingRef}
            tabIndex={-1}
            className="text-2xl font-bold text-white mb-2 focus:outline-none"
          >
            You&rsquo;re signed in
          </h2>
          {continueError ? (
            <p className="text-gray-400 text-sm leading-relaxed" role="alert" aria-live="assertive">
              {continueError}
            </p>
          ) : (
            <p className="text-gray-400 text-sm leading-relaxed">Taking you to VALUTTEN&hellip;</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => void continueAfterSignIn(signedIn)}
          disabled={verifying}
          className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
          aria-busy={verifying}
        >
          {verifying ? (
            <>
              <Spinner size="sm" className="mr-2" />
              <span>Continuing&hellip;</span>
            </>
          ) : (
            // Nothing has failed yet: this is the way onward, not a retry.
            <span>{continueError ? 'Try again' : 'Continue to VALUTTEN'}</span>
          )}
        </button>
        {continueError && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="block w-full text-center mt-4 text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-200"
          >
            <span className="border-b border-gray-500/40">Reload the page</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700">
      <div className="text-center mb-6">
        <div
          className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
            failure
              ? 'bg-gradient-to-r from-red-500 to-orange-500'
              : 'bg-gradient-to-r from-emerald-500 to-green-500'
          }`}
        >
          {failure ? (
            <AlertTriangle size={28} className="text-white" />
          ) : (
            <Lock size={28} className="text-white" />
          )}
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Enter your code</h2>
        <p className="text-gray-400 text-sm leading-relaxed">
          Open your authenticator app and enter the current 6-digit code for VALUTTEN.
        </p>
      </div>

      <MfaCodeInput
        ariaLabel="Sign-in verification code"
        autoFocus
        onChange={setCode}
        onComplete={(v) => void handleVerify(v)}
        disabled={verifying || isTerminal || signedIn !== null}
        error={isWrongCode}
        resetNonce={resetNonce}
      />
      <p
        className={`text-xs text-center mt-2 mb-5 min-h-[1rem] ${failure ? 'text-red-400' : 'text-gray-500'}`}
        role={failure ? 'alert' : undefined}
        aria-live={failure ? 'assertive' : undefined}
      >
        {failure?.message || 'Codes refresh every 30 seconds'}
      </p>

      {failure?.kind === 'session_expired' || failure?.kind === 'requires_recent_login' ? (
        <button
          type="button"
          onClick={() => window.location.assign('/login')}
          className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <span>Back to sign in</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => void handleVerify()}
          disabled={code.length !== 6 || verifying || isTerminal}
          className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
          aria-busy={verifying}
        >
          {verifying ? (
            <>
              <Spinner size="sm" className="mr-2" />
              <span>Verifying&hellip;</span>
            </>
          ) : (
            <span>Verify and sign in</span>
          )}
        </button>
      )}

      <button
        type="button"
        onClick={() => setShowRecovery(true)}
        className="block w-full text-center mt-4 text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-200"
      >
        <span className="border-b border-gray-500/40">Can&rsquo;t access your authenticator?</span>
      </button>
    </div>
  );
};

export default MfaChallengeCard;
